import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Dropdown, Text, Option, Field, Input, Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell, Tooltip, Title1, Title3, Subtitle2, Subtitle1 } from "@fluentui/react-components";
import { BinRecycle24Regular, Dismiss24Regular } from "@fluentui/react-icons";
import { FC, useEffect, useRef, useState } from "react";
import { TenderFeePeriodReport, TenderFeeReport, TenderFeeStatuses, TenderingFee, TenderingFeeMeasure } from "../../models/tendering";
import { useForm, Controller } from "react-hook-form";
import { useAppContext } from "../../AppContext";
import { deepCloneObject } from "../../utils/clone";
import ConfirmationDialog from "./ConfirmationDialog";

interface Props {
  open: boolean;
  onClose: () => void;
  feePeriodReport: TenderFeePeriodReport;
  tenderingFee: TenderingFee;
  saveReport: (r: TenderFeePeriodReport, sendBill: boolean) => void;
  tenderingName: string;
  notifyText: string;
}

const ReportDialog: FC<Props> = ({ open, onClose, feePeriodReport, tenderingFee, saveReport, tenderingName, notifyText }) => {
  const procurers = [
    {organizationID: 15, organizationName: 'Älmhult'},
    {organizationID: 16, organizationName: 'Alvesta'},
    {organizationID: 17, organizationName: 'Åstorp'},
    {organizationID: 18, organizationName: 'Båstad'},
    {organizationID: 19, organizationName: 'Bjuv'},
    {organizationID: 20, organizationName: 'Bromölla'},
    {organizationID: 21, organizationName: 'Burlöv'},
    {organizationID: 22, organizationName: 'Ängelholm'},
    {organizationID: 23, organizationName: 'Eslöv'},
    {organizationID: 24, organizationName: 'Falkenberg'},
    {organizationID: 25, organizationName: 'Halmstad'},
    {organizationID: 26, organizationName: 'Hässleholm'},
    {organizationID: 27, organizationName: 'Helsingborg'},
    {organizationID: 28, organizationName: 'Höganäs'},
    {organizationID: 29, organizationName: 'Höör'},
    {organizationID: 30, organizationName: 'Hörby'},
    {organizationID: 31, organizationName: 'Hylte'},
    {organizationID: 32, organizationName: 'Kävlinge'},
    {organizationID: 33, organizationName: 'Klippan'},
    {organizationID: 34, organizationName: 'Kristianstad'},
    {organizationID: 35, organizationName: 'Laholm'},
    {organizationID: 36, organizationName: 'Landskrona'},
    {organizationID: 37, organizationName: 'Lessebo'},
    {organizationID: 39, organizationName: 'Ljungby'},
    {organizationID: 40, organizationName: 'Lomma'},
    {organizationID: 41, organizationName: 'Lund'},
    {organizationID: 42, organizationName: 'Malmö'},
    {organizationID: 43, organizationName: 'Markaryd'},
    {organizationID: 44, organizationName: 'Örkelljunga'},
    {organizationID: 45, organizationName: 'Osby'},
    {organizationID: 46, organizationName: 'Östra Göinge'},
    {organizationID: 47, organizationName: 'Perstorp'},
    {organizationID: 48, organizationName: 'Simrishamn'},
    {organizationID: 49, organizationName: 'Sjöbo'},
    {organizationID: 50, organizationName: 'Skurup'},
    {organizationID: 51, organizationName: 'Staffanstorp'},
    {organizationID: 52, organizationName: 'Svalöv'},
    {organizationID: 53, organizationName: 'Svedala'},
    {organizationID: 54, organizationName: 'Tingsryd'},
    {organizationID: 55, organizationName: 'Tomelilla'},
    {organizationID: 56, organizationName: 'Trelleborg'},
    {organizationID: 57, organizationName: 'Uppvidinge'},
    {organizationID: 58, organizationName: 'Varberg'},
    {organizationID: 59, organizationName: 'Växjö'},
    {organizationID: 60, organizationName: 'Vellinge'},
    {organizationID: 61, organizationName: 'Ystad'}
  ];

  const {user} = useAppContext();
  const [tenderFeeReport, setTenderFeeReport] = useState<TenderFeePeriodReport>(feePeriodReport);
  const [prelFee, setPrelFee] = useState<number>(0);
  const [editReport, setEditReport] = useState<boolean>(feePeriodReport.status === TenderFeeStatuses.Notified.status);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
  const initialReport = useRef(deepCloneObject(feePeriodReport.report));
  const hasChanges = JSON.stringify(tenderFeeReport.report) !== JSON.stringify(initialReport.current);

  const handleClose = () => {
    if (editReport && hasChanges) {
      setShowConfirmationDialog(true);
    } else {
      onClose();
    }
  };

  const { control, handleSubmit, reset, watch } = useForm<{
    procurerId: string;
    measureId: string;
    quantity: number;
    value: number;
  }>({
    defaultValues: {
      procurerId: '',
      measureId: '',
      quantity: 0,
      value: 0
    }
  });

  const formValues = watch();
  const isFormValid = formValues.procurerId !== '' && 
                      formValues.measureId !== '' && 
                      formValues.quantity > 0 && 
                      formValues.value > 0;

  const onSubmit = (data: any) => {
    const selectedProcurer = procurers.find((p) => p.organizationID.toString() === data.procurerId);
    const selectedMeasure = tenderingFee.measures?.find((m) => m.id === data.measureId);
    
    if (selectedProcurer && selectedMeasure) {
      const newReport: TenderFeeReport = {
        procurer: selectedProcurer,
        measure: selectedMeasure,
        quantity: data.quantity,
        value: data.value,
        user: user.user, // You may need to set this properly
        price: 0 // Calculate if needed
      };

      setTenderFeeReport((prev) => ({
        ...prev,
        report: [...(prev.report || []), newReport]
      }));

      reset();
    }
  };

  useEffect(() => {
    calculateFee();
  }, [tenderFeeReport]);

  const calculateFee = () => {
    setPrelFee(0);
    if (!tenderFeeReport) {
      return;
    }

    let amount = 0;
    tenderFeeReport.report?.forEach(r=> {
      if (r.measure.feeType === 'Procent') {
        let cost = r.value;
        cost = (cost/100)*r.measure.feeValue;
        amount += cost;
      }
      else {
        amount += r.value;
      }
    });
 
    setPrelFee((prev) => {
      let newFee = prev + amount;
      return Number(newFee.toFixed(2));
    });
  }

  const calculateTableFee = (r: TenderFeeReport) => {
    let fee = 0;
    if (r.measure.feeType === 'Procent') {
      fee = ((r.value)/100) * r.measure.feeValue;
    }
    else {
      fee = r.value;
    }
    fee = Number(fee.toFixed(2));
    return fee;
  }

  const removeReport = (index: number) => {
    setTenderFeeReport((prev) => ({
      ...prev,
      report: prev.report?.filter((_, i) => i !== index) || []
    }));
  }

  const resetTenderFee = () : void => {
    let resetReport = feePeriodReport;
    resetReport.status = TenderFeeStatuses.Notified.status;
    resetReport.nextState = TenderFeeStatuses.Notified.next;
    resetReport.sent = false;

    saveReport(resetReport, false);
  }

  return (
    <>
    <Dialog modalType="alert" open={open} onOpenChange={handleClose}>
      <DialogSurface style={{minWidth: '950px'}}>
        <DialogBody>
          <DialogTitle>
            <div className="flex justify-between items-center">
              <Title3>
                Skicka in rapport
              </Title3>
              <Button
                appearance="subtle"
                shape="circular"
                icon={<Dismiss24Regular />}
                onClick={handleClose}
                aria-label="Stäng"
              />
            </div>
          </DialogTitle>
          <DialogContent>
            {editReport && (
              <div className="flex flex-col gap-3 rich-text-content" dangerouslySetInnerHTML={{__html: notifyText || ''}}></div>)}
            {editReport &&(
              <div>
                
              </div>
            )}
            {editReport && (<div className="my-5">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="flex gap-3 items-end">
                  <div className="min-w-[180px] flex-1">
                    <Controller
                      name="procurerId"
                      control={control}
                      render={({ field }) => (
                        <Field label="Välj kommun">
                          <Dropdown
                            value={field.value ? procurers.find(p => p.organizationID.toString() === field.value)?.organizationName : ''}
                            selectedOptions={field.value ? [field.value] : []}
                            style={{ width: '100%', minWidth: '180px' }}
                            positioning={{autoSize: false}}
                            listbox={{
                              style: {
                                maxHeight: '300px',
                                overflowY: 'auto'
                              },
                            }}
                            onOptionSelect={(_, data) => {
                              if (data.optionValue !== undefined) {
                                field.onChange(data.optionValue);
                              }
                            }}
                            >
                            {procurers.map((procurer) => (
                              <Option key={procurer.organizationID} value={procurer.organizationID.toString()} text={procurer.organizationName}>
                                {procurer.organizationName}
                              </Option>
                            ))}
                          </Dropdown>
                        </Field>
                      )}
                    />
                  </div>
                  <div className="min-w-[200px] flex-1">
                    <Controller
                      name="measureId"
                      control={control}
                      render={({ field }) => (
                        <Field label="Välj delområde">
                          <Dropdown
                            value={field.value ? tenderingFee.measures?.find(m => m.id === field.value)?.header : ''}
                            selectedOptions={field.value ? [field.value] : []}
                            style={{ width: '100%', minWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            positioning={{autoSize: false}}
                            listbox={{
                              style: {
                                maxHeight: '300px',
                                overflowY: 'auto'
                              },
                            }}
                            onOptionSelect={(_, data) => {
                              if (data.optionValue !== undefined) {
                                field.onChange(data.optionValue);
                              }
                            }}
                            >
                            {tenderingFee.measures?.map((measure: TenderingFeeMeasure) => (
                              <Option key={measure.id} value={measure.id} text={measure.header}>
                                {measure.header}
                              </Option>
                            ))}
                          </Dropdown>
                        </Field>
                      )}
                    />
                  </div>
                  <div className="min-w-[120px]">
                    <Controller
                      name="quantity"
                      control={control}
                      render={({ field }) => (
                        <Field label={`Sammanlagt antal ${tenderingFee.measures?.find(m => m.id === formValues.measureId)?.valueType.toLocaleLowerCase() || ''}:`}>
                          <Input 
                            type="number" 
                            min={0} 
                            value={field.value.toString()}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                            style={{ width: '100%' }} 
                          /> 
                        </Field>
                      )}
                    />
                  </div>
                  <div className="min-w-[150px]">
                    <Controller
                      name="value"
                      control={control}
                      render={({ field }) => (
                        <Field label="Total intäkt från kommun:">
                          <Input 
                            type="number" 
                            min={0} 
                            value={field.value.toString()}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                            style={{ width: '100%' }} 
                          /> 
                        </Field>
                      )}
                    />
                  </div>
                  <Button type="submit" style={{ alignSelf: 'flex-end' }} disabled={!isFormValid}>
                    Lägg till
                  </Button>
                </div>
              </form>
            </div>)}
            <div className="max-h-[50vh] overflow-y-auto">
              <Table size="small" aria-label="Förfrågningar tabell">
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>
                      Kommun
                    </TableHeaderCell>
                    <TableHeaderCell className="w-1/4">
                      Delområde
                    </TableHeaderCell>
                    <TableHeaderCell className="w-1/5">
                      Rubrik
                    </TableHeaderCell>
                    <TableHeaderCell>
                      Antal
                    </TableHeaderCell>
                    <TableHeaderCell>
                      Intäkt
                    </TableHeaderCell>
                    <TableHeaderCell>
                      Andel
                    </TableHeaderCell>
                    <TableHeaderCell>
                      Avgift
                    </TableHeaderCell>
                    {editReport && <TableHeaderCell>
                    </TableHeaderCell>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenderFeeReport.report?.map((r, index) => (
                    <TableRow className="group" key={index}>
                      <TableCell>
                        {r.procurer.organizationName}
                      </TableCell>
                      <TableCell>
                        {r.measure.header}
                      </TableCell>
                      <TableCell>
                        <Text weight="semibold">{tenderingName} - {r.procurer.organizationName}</Text>
                      </TableCell>
                      <TableCell>
                        {r.quantity}
                      </TableCell>
                      <TableCell>
                        {r.value}
                      </TableCell>
                      <TableCell>
                        {r.measure.feeType === 'Procent' ? `${r.measure.feeValue}%` : `${r.measure.feeValue} kr`}
                      </TableCell>
                      <TableCell>
                        {calculateTableFee(r)} kr
                      </TableCell>
                      {editReport && <TableCell>
                        <Tooltip content="Radera rad" relationship={"label"}>
                          <Button
                            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-1/80"
                            shape="circular"
                            icon={<BinRecycle24Regular />}
                            onClick={() => removeReport(index)}
                            aria-label="Radera rad"
                          />
                        </Tooltip>
                      </TableCell>}
                    </TableRow>))}
                </TableBody>
              </Table>
            </div>
            {editReport && (<div className="mt-4">
              <Text weight="semibold">Preliminär avgift exkl moms:</Text>
              <Field className="col-1">
                <Input value={prelFee.toString()} disabled></Input>
              </Field>
            </div>)}
            {editReport ? (
              <DialogActions className="mt-4">
                <Button onClick={() => saveReport(tenderFeeReport, false)}>Spara utkast</Button>
                <Button appearance="primary" onClick={() => saveReport(tenderFeeReport, true)}>{tenderFeeReport && tenderFeeReport.report && tenderFeeReport.report.length > 0 ? 'Skicka in rapport' : 'Skicka in rapport med svar utan intäkt'}</Button>
              </DialogActions>) : (
              <DialogActions className="mt-4">
                <Button disabled={feePeriodReport.status == "Fakturerad" && user.roles.some((role: any) => role.roleType === 'General')} onClick={resetTenderFee} >Ångra/Neka inrapportering</Button>
                <Button appearance="primary" onClick={() => {JSON.stringify(initialReport.current) === JSON.stringify(tenderFeeReport) ? onClose() : setShowConfirmationDialog(true)}}>Stäng</Button>
              </DialogActions>
            )}
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
    <ConfirmationDialog 
      isOpen={showConfirmationDialog}
      title="Bekräfta åtgärd"
      text="Du har gjort ändringar i rapporten. Vill du spara ändringarna?"
      onConfirm={() => {
        saveReport(tenderFeeReport, true);
        setShowConfirmationDialog(false);
        onClose();
      }}
      onClose={() => {setShowConfirmationDialog(false); onClose();}}
    />
    </>
  )
}

export default ReportDialog;